import styles from './table.module.css'
import { Button } from "../button/button"
import { define, toDate } from '../../../../utils/typeUtils'
import { Pagination } from '../pagination/pagination'
import { useEffect, useMemo, useState } from 'react'
import { Action, Column, Data, FieldType, FieldValue } from '../types'
import { StaticValue } from '../static-value/staticValue'
import { ActionButton } from '../action-button/actionButton'
import { Spinner } from '../spinner/spinner'
import { O } from '../../state/types'

type Props = {
    columns: Column[]
    rows: Data[]
    rowId: string
    pageSize?: number
    // Opcionales, para controlar la página desde afuera; si no se pasan la tabla la maneja sola
    page?: number
    onPageChange?: (page: number) => void
    readOnly?: boolean
    disableCreate?: boolean
    small?: boolean
    createLabel?: string
    createIcon?: string
    createTooltip?: string | string[]
    focusCreate?: boolean
    alignRight?: boolean
    className?: string
    createHref?: string
    onCreate?: () => void
    onEdit?: (rowIdValue: string) => void
    onRemove?: (rowIdValue: string) => void
    actions?: (rowIdValue: string) => Action[]
    renderActions?: (rowIdValue: string) => JSX.Element
    renderDetail?: (row: Data) => JSX.Element
}

export const Table = (props: Props) => {
    const { columns, rows, rowId, pageSize = 10, page, onPageChange, readOnly, disableCreate, small, createLabel, createIcon, createTooltip, focusCreate, createHref, onCreate, onEdit, onRemove, actions, renderActions, renderDetail, className, alignRight = true } = props
    const [internalPage, setInternalPage] = useState(1)
    const currentPage = page ?? internalPage
    const setCurrentPage = (newPage: number) => onPageChange ? onPageChange(newPage) : setInternalPage(newPage)
    const [openedRow, setOpenedRow] = useState<string>()
    
    const pagesAmount = Math.ceil(rows.length / pageSize)
    const showActionsColumn = !readOnly || actions || renderActions || renderDetail
    const columnsAmount = columns.length + (showActionsColumn ? 1 : 0)
    const pageRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize
        return rows.slice(start, start + pageSize)
    }, [currentPage, rows])
    const isTooltipString = typeof createTooltip === "string"

    const goToPreviousPage = () => setCurrentPage(currentPage - 1)

    useEffect(() => {
        pageRows.length === 0 && currentPage > 1 && goToPreviousPage()
    }, [pageRows, currentPage])

    const defaultRender = (column: Column, rawValue?: FieldValue) => {
        const value = column.type === FieldType.DATE ? toDate(rawValue as O<string>) : rawValue
        return <StaticValue type={column.type} table={column.table} value={value} />
    }

    const onActionsClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation()

    const renderActionBtn = (rowIdValue: string) => (action: Action, index: number) => {
        const onClick = action.onClick ? () => action.onClick!(rowIdValue) : undefined
        const actionClassName = actions && index < actions(rowIdValue).length - 1 ? styles.actionBtn : ''
        return <ActionButton
            key={index}
            action={action}
            className={actionClassName}
            small={action.small}
            onClick={onClick}
        />
    }

    const renderEditBtn = (rowIdValue: string) => {
        const onEditRow = () => onEdit && onEdit(rowIdValue)
        return <Button
            icon="pencil-fill"
            tooltip='Editar'
            small={small}
            rightMargin
            focusable={false}
            onClick={onEditRow}
        />
    }
    
    const renderRemoveBtn = (rowIdValue: string) => {
        const onRemoveRow = () => onRemove && onRemove(rowIdValue)
        return <Button
            kind='secondary'
            icon='trash-fill'
            tooltip='Eliminar'
            small={small}
            focusable={false}
            onClick={onRemoveRow}
        />
    }

    const toggleDetail = (rowIdValue: string) => () => {
        renderDetail && setOpenedRow(prevValue => prevValue === rowIdValue ? undefined : rowIdValue)
    }

    return (
        <>  
            <Pagination
                pagesAmount={pagesAmount}
                page={currentPage}
                onGoToPage={setCurrentPage}
            />
            <table className={`table ${openedRow ? '' : 'table-hover'} ${className || ''}`}>
                <thead>
                    <tr className={alignRight ? styles.alignRight : ''}>
                        {columns.map(colum => (
                            <th key={colum.name} scope="col" className={styles.th}>
                                {rows.length > 0 ? (colum.renderLabel ? colum.renderLabel() : colum.label) : ''}
                            </th>
                        ))}
                        {showActionsColumn && (
                            <th scope="col">
                                {!readOnly && (onCreate || createHref) && (
                                    <div className={styles.actionsContainer}>
                                        <Button
                                            label={createLabel || 'Agregar'}
                                            icon={createIcon}
                                            small={small}
                                            disabled={disableCreate}
                                            tooltip={isTooltipString ? createTooltip : undefined}
                                            tooltipLines={!isTooltipString ? createTooltip : undefined}
                                            focus={focusCreate}
                                            href={createHref}
                                            onClick={onCreate}
                                            className={styles.createBtn}
                                        />
                                    </div>
                                )}
                            </th>
                        )}
                    </tr>
                </thead>
                {pageRows.map(row => {
                    const rowIdValue = define(row[rowId]).toString() as string
                    const showDetail = openedRow === rowIdValue
                    const detail = renderDetail && renderDetail(row)
                    const expandedClass = showDetail ? styles.expanded : ''
                    
                    return (
                        <tbody key={rowIdValue}>
                            <tr onClick={toggleDetail(rowIdValue)} className={alignRight ? styles.alignRight : ''}>
                                {columns.map(column => {
                                    const value = row[column.name]
                                    const tdClass = `${styles.td} ${column.type === FieldType.IMAGE ? styles.imgTd : ''}`
                                    return (
                                        <td key={column.name} className={tdClass}>
                                            {column.loading ? <Spinner centered={false} /> : (
                                                column.render ? column.render(value) : defaultRender(column, value)
                                            )}
                                        </td>
                                    )
                                })}
                                {(showActionsColumn || !!detail) && (
                                    <td className={`${styles.td} ${styles.actionsTd}`}>
                                        <div className={styles.actionsContainer} onClick={onActionsClick}>
                                            {actions ? actions(rowIdValue).map(renderActionBtn(rowIdValue)) : (
                                                !readOnly && (
                                                    <>
                                                        {onEdit && renderEditBtn(rowIdValue)}
                                                        {onRemove && renderRemoveBtn(rowIdValue)}
                                                    </>
                                                )
                                            )}
                                            {renderActions && renderActions(rowIdValue)}
                                            {detail && <i
                                                onClick={toggleDetail(rowIdValue)}
                                                className={`bi bi-chevron-down ${styles.expandIcon} ${expandedClass}`}
                                            />}
                                        </div>
                                    </td>
                                )}
                            </tr>
                            {detail && showDetail && (
                                <tr className={styles.detailTr}>
                                    <td colSpan={columnsAmount}>
                                        {detail}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    )
                })}
            </table>
            <Pagination
                pagesAmount={pagesAmount}
                page={currentPage}
                onGoToPage={setCurrentPage}
            />
        </>
    )
}
